import React from 'react';
import { Link } from 'react-router-dom';
import { 
  BookOpen, 
  Users, 
  Briefcase, 
  Target, 
  CheckCircle, 
  Clock, 
  Star,
  ArrowRight,
  PlayCircle,
  Award
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const FormationPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="/logo-originale.png" 
                alt="InLeads Logo" 
                className="w-8 h-8 object-contain"
              />
              <span className="text-2xl font-bold text-gray-900">InLeads</span>
            </Link>
            
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-600 hover:text-[#0047FF] transition-colors">
                Home
              </Link>
              <Link to="/blog" className="text-gray-600 hover:text-[#0047FF] transition-colors">
                Blog
              </Link>
              <Link to="/formazione" className="text-[#0047FF] font-semibold">
                Formazione
              </Link>
              <Link to="/contatti" className="text-gray-600 hover:text-[#0047FF] transition-colors">
                Contatti
              </Link>
              <a href="https://app.inleads.it/#/accesso" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-[#0047FF] transition-colors">
                Accedi all'App
              </a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#0047FF]/10 text-[#0047FF] border-[#0047FF]/20">
              Formazione Professionale
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Formazione InLeads
            </h1>
            <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto">
              Servizi formativi specializzati per agenzie e candidati del settore immobiliare. 
              Cresci professionalmente con il supporto di esperti del settore.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Perché scegliere la formazione InLeads?
              </h2>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Formatori esperti del settore immobiliare</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Contenuti pratici e aggiornati al mercato</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Certificazioni riconosciute nel settore</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Supporto continuo e mentoring personalizzato</span>
                </li>
              </ul>
            </div>
            <div className="text-center">
              <img 
                src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=600&h=400&fit=crop&crop=center"
                alt="Formazione professionale"
                className="w-full h-64 object-cover rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Formazione per Candidati */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Formazione per Candidati
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Preparati al meglio per la tua carriera nel settore immobiliare con corsi gratuiti e specializzati
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <Card className="border-2 hover:border-[#0047FF] transition-colors">
              <CardHeader>
                <div className="w-12 h-12 bg-gradient-to-br from-[#0047FF] to-blue-600 rounded-lg flex items-center justify-center mb-4">
                  <Target className="h-6 w-6 text-white" />
                </div>
                <CardTitle>Preparazione Colloqui</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Impara a presentarti al meglio durante i colloqui con le agenzie immobiliari
                </p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• Tecniche di comunicazione efficace</li>
                  <li>• Come valorizzare il tuo profilo</li>
                  <li>• Gestione dello stress e sicurezza</li>
                  <li>• Simulazioni pratiche</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-[#0047FF] transition-colors">
              <CardHeader>
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center mb-4">
                  <BookOpen className="h-6 w-6 text-white" />
                </div>
                <CardTitle>Fondamenti Immobiliari</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Le basi del settore immobiliare per iniziare con competenza
                </p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• Legislazione immobiliare essenziale</li>
                  <li>• Valutazione immobiliare base</li>
                  <li>• Dinamiche del mercato locale</li>
                  <li>• Etica professionale</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-[#0047FF] transition-colors">
              <CardHeader>
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center mb-4">
                  <Star className="h-6 w-6 text-white" />
                </div>
                <CardTitle>Sviluppo Carriera</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Strategie per accelerare la tua crescita professionale
                </p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• Networking efficace</li>
                  <li>• Personal branding</li>
                  <li>• Definizione obiettivi di carriera</li>
                  <li>• Mentoring e crescita continua</li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            <Button 
              asChild
              size="lg" 
              className="bg-gradient-to-r from-[#0047FF] to-blue-600 text-white px-8 py-4 rounded-xl hover:shadow-2xl transition-all duration-300"
            >
              <a href="https://app.inleads.it/#/accesso" target="_blank" rel="noopener noreferrer">
                <Users className="mr-3 h-5 w-5" />
                Accedi alla formazione gratuita
              </a>
            </Button>
            <p className="text-sm text-gray-600 mt-3">100% Gratuito • Certificazione inclusa</p>
          </div>
        </div>
      </section>

      {/* Formazione per Agenzie */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Formazione per Agenzie
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Migliora i processi di reclutamento e gestione del team con la formazione specializzata InLeads
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 mb-16">
            <Card className="bg-white border-2 hover:border-[#0047FF] transition-colors">
              <CardHeader>
                <div className="w-12 h-12 bg-gradient-to-br from-[#0047FF] to-blue-600 rounded-lg flex items-center justify-center mb-4">
                  <Briefcase className="h-6 w-6 text-white" />
                </div>
                <CardTitle>Ottimizzazione Reclutamento</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-6">
                  Strategie avanzate per attrarre e selezionare i migliori candidati
                </p>
                <ul className="text-sm text-gray-600 space-y-3 mb-6">
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>Tecniche di selezione efficaci</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>Employer branding nel settore immobiliare</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>Gestione colloqui e valutazioni</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>Onboarding e retention</span>
                  </li>
                </ul>
                <div className="flex items-center text-sm text-gray-600">
                  <Clock className="h-4 w-4 mr-2" />
                  <span>Durata: 6 ore - Modalità online</span>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-2 hover:border-[#0047FF] transition-colors">
              <CardHeader>
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center mb-4">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <CardTitle>Gestione Team Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-6">
                  Leadership e gestione delle performance per team immobiliari
                </p>
                <ul className="text-sm text-gray-600 space-y-3 mb-6">
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>Motivazione e incentivi efficaci</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>KPI e monitoring delle performance</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>Comunicazione e feedback continuo</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>Gestione conflitti e mediazione</span>
                  </li>
                </ul>
                <div className="flex items-center text-sm text-gray-600">
                  <Clock className="h-4 w-4 mr-2" />
                  <span>Durata: 8 ore - Modalità ibrida</span>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            <Button 
              asChild
              size="lg" 
              className="bg-gradient-to-r from-green-500 to-green-600 text-white px-8 py-4 rounded-xl hover:shadow-2xl transition-all duration-300"
            >
              <a href="https://app.inleads.it/#/demo" target="_blank" rel="noopener noreferrer">
                <Award className="mr-3 h-5 w-5" />
                Richiedi demo formazione aziendale
              </a>
            </Button>
            <p className="text-sm text-gray-600 mt-3">Consulenza gratuita • ROI garantito</p>
          </div>
        </div>
      </section>

      {/* Statistiche */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="text-3xl font-bold text-[#0047FF] mb-2">2.500+</div>
              <div className="text-gray-600">Candidati formati</div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="text-3xl font-bold text-green-600 mb-2">850+</div>
              <div className="text-gray-600">Agenzie partner</div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="text-3xl font-bold text-purple-600 mb-2">95%</div>
              <div className="text-gray-600">Tasso successo</div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="text-3xl font-bold text-orange-600 mb-2">4.8/5</div>
              <div className="text-gray-600">Rating medio</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-[#0047FF] to-blue-600 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Inizia il tuo percorso formativo oggi
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Unisciti a migliaia di professionisti che hanno accelerato la loro carriera con InLeads
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              size="lg" 
              className="bg-white text-[#0047FF] px-8 py-4 text-lg rounded-xl hover:shadow-2xl transition-all duration-300 font-bold"
            >
              <a href="https://app.inleads.it/#/accesso" target="_blank" rel="noopener noreferrer">
                Inizia ora - Gratuito
              </a>
            </Button>
            <Button 
              asChild
              size="lg" 
              variant="outline"
              className="border-2 border-white text-white px-8 py-4 text-lg rounded-xl hover:bg-white hover:text-[#0047FF] transition-all duration-300 font-bold"
            >
              <Link to="/contatti">
                Richiedi informazioni
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-gray-900 text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <img 
                src="/logo-originale.png" 
                alt="InLeads Logo" 
                className="w-8 h-8 object-contain brightness-0 invert"
              />
              <span className="text-2xl font-bold">InLeads</span>
            </div>
            <p className="text-gray-400 mb-6">
              La piattaforma che connette agenzie e talenti nel settore immobiliare
            </p>
          </div>

          <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-8">
            <Link to="/" className="text-gray-400 hover:text-white transition-colors">
              Home
            </Link>
            <Link to="/blog" className="text-gray-400 hover:text-white transition-colors">
              Blog
            </Link>
            <Link to="/formazione" className="text-gray-400 hover:text-white transition-colors">
              Formazione
            </Link>
            <Link to="/contatti" className="text-gray-400 hover:text-white transition-colors">
              Contatti
            </Link>
            <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">
              Privacy Policy
            </Link>
          </div>

          <div className="text-center mt-8 pt-6 border-t border-gray-700">
            <p className="text-sm text-gray-500">
              © 2025 InLeads. Tutti i diritti riservati.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FormationPage;